import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, Input, Label } from '../components/ui';
import { showToast } from '../utils/toast';
import {
  reconciliationService,
  ReconciliationRunListItem,
  ReconciliationRunStartRequest,
  ReconciliationRunStatus,
  TenantWarehouseMetrics,
} from '../services/reconciliation.service';

const ENTITY_OPTIONS = [
  'clio-activities',
  'clio-activity-descriptions',
  'clio-bank-transactions',
  'clio-bills',
  'clio-contacts',
  'clio-line-items',
  'clio-matters',
  'clio-wip',
] as const;

interface RunFormValues {
  tenantId: string;
  allTenants: boolean;
  entities: string[];
  dryRun: boolean;
  maxHeal: number;
  updatedAtLagThresholdMinutes: number;
  forceClosedMonths: boolean;
  healEnabled: boolean;
}

export function ReconciliationRunForm({
  values,
  onChange,
  onSubmit,
  submitting,
}: {
  values: RunFormValues;
  onChange: (next: RunFormValues) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const toggleEntity = (entity: string): void => {
    const has = values.entities.includes(entity);
    const next = has
      ? values.entities.filter(item => item !== entity)
      : [...values.entities, entity];

    onChange({ ...values, entities: next });
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">Start Reconciliation Run</h2>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tenant-id">Tenant ID</Label>
            <Input
              id="tenant-id"
              value={values.tenantId}
              placeholder="0000000001"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...values, tenantId: event.target.value })
              }
              disabled={values.allTenants}
            />
            <label className="flex items-center gap-2 text-sm text-xtnd-dark dark:text-xtnd-light">
              <input
                type="checkbox"
                checked={values.allTenants}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({ ...values, allTenants: event.target.checked })
                }
              />
              Run for all tenants
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-heal">Max Heal</Label>
            <Input
              id="max-heal"
              type="number"
              min={0}
              value={String(values.maxHeal)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...values, maxHeal: Number(event.target.value || 0) })
              }
            />

            <Label htmlFor="lag-threshold">Updated_at Lag Threshold (minutes)</Label>
            <Input
              id="lag-threshold"
              type="number"
              min={1}
              value={String(values.updatedAtLagThresholdMinutes)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...values, updatedAtLagThresholdMinutes: Number(event.target.value || 1) })
              }
            />
          </div>
        </div>

        <div className="mt-4">
          <Label>Entities</Label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="entity-options">
            {ENTITY_OPTIONS.map(entity => (
              <label key={entity} className="flex items-center gap-2 text-sm text-xtnd-dark dark:text-xtnd-light">
                <input
                  type="checkbox"
                  checked={values.entities.includes(entity)}
                  onChange={() => toggleEntity(entity)}
                />
                {entity}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-xtnd-dark dark:text-xtnd-light">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={values.dryRun}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...values, dryRun: event.target.checked })}
            />
            Dry Run
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={values.healEnabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...values, healEnabled: event.target.checked })}
            />
            Heal Enabled
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={values.forceClosedMonths}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...values, forceClosedMonths: event.target.checked })}
            />
            Force Closed Months
          </label>
        </div>

        <div className="mt-6">
          <Button onClick={onSubmit} loading={submitting}>
            Start Run
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function statusBadgeClasses(status: ReconciliationRunStatus): string {
  if (status === 'COMPLETED') return 'bg-green-100 text-green-800';
  if (status === 'FAILED') return 'bg-red-100 text-red-800';
  if (status === 'RUNNING') return 'bg-blue-100 text-blue-800';
  if (status === 'PAUSED') return 'bg-yellow-100 text-yellow-800';
  if (status === 'CANCEL_REQUESTED') return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-800';
}

export function ReconciliationRunsTable({ runs }: { runs: ReconciliationRunListItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left border-b border-xtnd-light dark:border-xtnd-dark-700">
            <th className="py-2">Run ID</th>
            <th className="py-2">Status</th>
            <th className="py-2">Scope</th>
            <th className="py-2">Progress</th>
            <th className="py-2">Created</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <tr key={run.runId} className="border-b border-xtnd-light/50 dark:border-xtnd-dark-700/40">
              <td className="py-2">
                <Link className="text-xtnd-blue hover:underline" to={`/reconciliation/${run.runId}`}>
                  {run.runId}
                </Link>
              </td>
              <td className="py-2">
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${statusBadgeClasses(run.status)}`}>
                  {run.status}
                </span>
              </td>
              <td className="py-2">{run.tenantScope}</td>
              <td className="py-2">{run.counts.completed + run.counts.failed + run.counts.cancelled}/{run.counts.total}</td>
              <td className="py-2">{new Date(run.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatTimestamp(value: string | null): string {
  if (!value) return 'N/A';

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;

  return new Date(parsed).toLocaleString();
}

function formatModeLabel(mode: TenantWarehouseMetrics['tables'][number]['mode']): string {
  if (mode === 'monthly-report') return 'Monthly report';
  if (mode === 'snapshot-report') return 'Snapshot report';
  return 'Dataset';
}

function formatIngestSource(column: TenantWarehouseMetrics['tables'][number]['ingestTimestampColumn']): string {
  if (!column) return 'N/A';
  return column;
}

export function WarehouseMetricsView({ metrics }: { metrics: TenantWarehouseMetrics }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
        <div className="rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-3">
          <div className="text-xtnd-dark-600 dark:text-xtnd-light">Tenant</div>
          <div className="mt-1 font-semibold text-xtnd-dark dark:text-xtnd-white">{metrics.tenantId}</div>
        </div>
        <div className="rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-3">
          <div className="text-xtnd-dark-600 dark:text-xtnd-light">Tables</div>
          <div className="mt-1 font-semibold text-xtnd-dark dark:text-xtnd-white">{metrics.tables.length}</div>
        </div>
        <div className="rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-3">
          <div className="text-xtnd-dark-600 dark:text-xtnd-light">Monthly Clio Reports</div>
          <div className="mt-1 font-semibold text-xtnd-dark dark:text-xtnd-white">{metrics.monthlyReports.length}</div>
        </div>
        <div className="rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-3">
          <div className="text-xtnd-dark-600 dark:text-xtnd-light">Generated</div>
          <div className="mt-1 font-semibold text-xtnd-dark dark:text-xtnd-white">{formatTimestamp(metrics.generatedAt)}</div>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-xtnd-dark dark:text-xtnd-white">Tenant Redshift Tables</h3>
        {metrics.tables.length === 0 && (
          <div className="mt-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light">
            No base tables were found in this tenant schema.
          </div>
        )}
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b border-xtnd-light dark:border-xtnd-dark-700">
                <th className="py-2">Table</th>
                <th className="py-2">Mode</th>
                <th className="py-2">Records</th>
                <th className="py-2">Latest ingest</th>
                <th className="py-2">Timestamp source</th>
              </tr>
            </thead>
            <tbody>
              {metrics.tables.map(table => (
                <tr key={table.tableName} className="border-b border-xtnd-light/50 dark:border-xtnd-dark-700/40">
                  <td className="py-2 font-medium text-xtnd-dark dark:text-xtnd-white">{table.tableName}</td>
                  <td className="py-2">{formatModeLabel(table.mode)}</td>
                  <td className="py-2">{table.recordCount.toLocaleString()}</td>
                  <td className="py-2">{formatTimestamp(table.maxIngestedAt)}</td>
                  <td className="py-2">{formatIngestSource(table.ingestTimestampColumn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-xtnd-dark dark:text-xtnd-white">Monthly Clio Report Breakdown</h3>
        {metrics.monthlyReports.length === 0 && (
          <div className="mt-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light">
            No month-mode `clio_report_*` tables found for this tenant.
          </div>
        )}
        <div className="mt-3 space-y-4">
          {metrics.monthlyReports.map(report => (
            <div
              key={report.tableName}
              className="rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-xtnd-dark dark:text-xtnd-white">{report.tableName}</h4>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">
                    {report.recordCount.toLocaleString()} rows total, latest ingest {formatTimestamp(report.maxIngestedAt)}
                  </p>
                </div>
                <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">
                  Source: {formatIngestSource(report.ingestTimestampColumn)}
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-xtnd-light dark:border-xtnd-dark-700">
                      <th className="py-2">reportMonthCode</th>
                      <th className="py-2">Records</th>
                      <th className="py-2">Latest ingest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.months.map(month => (
                      <tr
                        key={`${report.tableName}-${month.reportMonthCode ?? 'null'}`}
                        className="border-b border-xtnd-light/50 dark:border-xtnd-dark-700/40"
                      >
                        <td className="py-2">{month.reportMonthCode ?? '(null)'}</td>
                        <td className="py-2">{month.recordCount.toLocaleString()}</td>
                        <td className="py-2">{formatTimestamp(month.maxIngestedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WarehouseMetricsInspector() {
  const [tenantInput, setTenantInput] = React.useState('');
  const [selectedTenantId, setSelectedTenantId] = React.useState('');

  const metricsQuery = useQuery({
    queryKey: ['reconciliation-tenant-warehouse-metrics', selectedTenantId],
    queryFn: () => reconciliationService.getTenantWarehouseMetrics(selectedTenantId),
    enabled: Boolean(selectedTenantId),
  });

  const submitLookup = (): void => {
    const normalizedTenantId = tenantInput.trim().replace('tenant-', '');
    if (!normalizedTenantId) {
      showToast.error('Enter a tenantId to inspect warehouse metrics');
      return;
    }

    if (normalizedTenantId === selectedTenantId) {
      void metricsQuery.refetch();
      return;
    }

    setSelectedTenantId(normalizedTenantId);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">Tenant Warehouse Insights</h2>
            <p className="mt-1 text-sm text-xtnd-dark-600 dark:text-xtnd-light">
              Inspect Redshift table counts and latest ingest times for a single tenant, including month-level breakdowns for monthly Clio reports.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="0000000001"
              value={tenantInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTenantInput(event.target.value)}
            />
            <Button onClick={submitLookup} loading={metricsQuery.isFetching && Boolean(selectedTenantId)}>
              Load Metrics
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {!selectedTenantId && (
          <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">
            Enter a tenant ID to load warehouse metrics.
          </div>
        )}
        {metricsQuery.isLoading && selectedTenantId && (
          <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">Loading warehouse metrics...</div>
        )}
        {metricsQuery.isError && (
          <div className="text-sm text-red-600">
            Failed to load warehouse metrics: {(metricsQuery.error as Error).message}
          </div>
        )}
        {metricsQuery.data && <WarehouseMetricsView metrics={metricsQuery.data} />}
      </CardBody>
    </Card>
  );
}

export function ReconciliationRunsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [filters, setFilters] = React.useState<{ status?: ReconciliationRunStatus; tenantId?: string }>({});
  const [formValues, setFormValues] = React.useState<RunFormValues>({
    tenantId: '',
    allTenants: false,
    entities: [...ENTITY_OPTIONS],
    dryRun: true,
    maxHeal: 100,
    updatedAtLagThresholdMinutes: 120,
    forceClosedMonths: false,
    healEnabled: true,
  });

  const runsQuery = useQuery({
    queryKey: ['reconciliation-runs', filters],
    queryFn: () => reconciliationService.listRuns({ limit: 100, ...filters }),
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return 15000;
      const hasActive = data.runs.some(run => ['RUNNING', 'PAUSED', 'CANCEL_REQUESTED'].includes(run.status));
      return hasActive ? 4000 : 15000;
    },
  });

  const startRunMutation = useMutation({
    mutationFn: (payload: ReconciliationRunStartRequest) => reconciliationService.startRun(payload),
    onSuccess: result => {
      showToast.success(`Run ${result.runId} started`);
      queryClient.invalidateQueries({ queryKey: ['reconciliation-runs'] });
      navigate(`/reconciliation/${result.runId}`);
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to start run');
    },
  });

  const submitRun = (): void => {
    const hasTenantId = formValues.tenantId.trim().length > 0;
    if ((hasTenantId && formValues.allTenants) || (!hasTenantId && !formValues.allTenants)) {
      showToast.error('Provide either tenantId or allTenants');
      return;
    }

    if (formValues.entities.length === 0) {
      showToast.error('Select at least one entity');
      return;
    }

    startRunMutation.mutate({
      tenantId: hasTenantId ? formValues.tenantId.replace('tenant-', '') : undefined,
      allTenants: formValues.allTenants,
      entities: formValues.entities,
      dryRun: formValues.dryRun,
      maxHeal: formValues.maxHeal,
      updatedAtLagThresholdMinutes: formValues.updatedAtLagThresholdMinutes,
      forceClosedMonths: formValues.forceClosedMonths,
      healEnabled: formValues.healEnabled,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white">Admin Reconciliation</h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light mt-1">
          Start dry-run/execute reconciliation jobs and inspect entity-level lag and delta metrics.
        </p>
      </div>

      <ReconciliationRunForm
        values={formValues}
        onChange={setFormValues}
        onSubmit={submitRun}
        submitting={startRunMutation.isPending}
      />

      <WarehouseMetricsInspector />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">Recent Runs</h2>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filter tenantId"
                value={filters.tenantId || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters(prev => ({ ...prev, tenantId: event.target.value || undefined }))
                }
              />
              <select
                className="px-3 py-2 rounded border border-xtnd-light dark:border-xtnd-dark-700 bg-xtnd-white dark:bg-xtnd-dark-800"
                value={filters.status || ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setFilters(prev => ({ ...prev, status: (event.target.value || undefined) as ReconciliationRunStatus | undefined }))
                }
              >
                <option value="">All Statuses</option>
                <option value="RUNNING">RUNNING</option>
                <option value="PAUSED">PAUSED</option>
                <option value="CANCEL_REQUESTED">CANCEL_REQUESTED</option>
                <option value="CANCELLED">CANCELLED</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {runsQuery.isLoading && <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">Loading runs...</div>}
          {runsQuery.isError && (
            <div className="text-sm text-red-600">Failed to load runs: {(runsQuery.error as Error).message}</div>
          )}
          {runsQuery.data && runsQuery.data.runs.length === 0 && (
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">No runs found.</div>
          )}
          {runsQuery.data && runsQuery.data.runs.length > 0 && (
            <ReconciliationRunsTable runs={runsQuery.data.runs} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default ReconciliationRunsPage;
